import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';

// Helper function to parse date string (YYYY-MM-DD) as local date without timezone conversion
const formatLocalDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const localDate = new Date(year, month - 1, day);
  return localDate.toLocaleDateString('he-IL');
};

const Reports = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverReport, setDriverReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [finalizedInfo, setFinalizedInfo] = useState(null);
  const [finalizingMonth, setFinalizingMonth] = useState(false);
  const [editingDistanceSupplement, setEditingDistanceSupplement] = useState({});
  const [updatingDistanceSupplement, setUpdatingDistanceSupplement] = useState(false);
  const [showDropsModal, setShowDropsModal] = useState(false);
  const [selectedDayDrops, setSelectedDayDrops] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // Auto-load current month report on mount
  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const [summaryResponse, finalizedResponse] = await Promise.all([
        api.get(`/salaries/summary/${month}/${year}`),
        api.get(`/salaries/check-finalized/${month}/${year}`)
      ]);
      setSummary(summaryResponse.data);
      setIsFinalized(finalizedResponse.data.is_finalized);
      setFinalizedInfo(finalizedResponse.data);
    } catch (error) {
      alert(error.response?.data?.error || 'שגיאה בטעינת דוח');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        alert('נדרש להתחבר מחדש');
        window.location.href = '/login';
        return;
      }

      const baseURL = api.defaults.baseURL || '/api';
      const url = `${baseURL}/salaries/export/${month}/${year}?token=${encodeURIComponent(token)}`;
      
      // Simple approach: open URL directly (browser will handle download)
      // This works because we added token support in query params
      window.location.href = url;
      
    } catch (error) {
      console.error('Export error:', error);
      alert(error.message || 'שגיאה בייצוא דוח');
    }
  };

  const loadDriverReport = async (driverId) => {
    setLoadingReport(true);
    setSelectedDriver(driverId);
    try {
      const response = await api.get(`/salaries/driver-report/${driverId}/${month}/${year}`);
      setDriverReport(response.data);
    } catch (error) {
      alert(error.response?.data?.error || 'שגיאה בטעינת דוח נהג');
      setSelectedDriver(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const closeDriverReport = () => {
    setSelectedDriver(null);
    setDriverReport(null);
    setEditingDistanceSupplement({});
    setShowDropsModal(false);
    setSelectedDayDrops(null);
  };

  const handleShowDropsDetails = (day) => {
    if (day.type2.drops > 0 && day.type2.drops_details) {
      setSelectedDayDrops({
        date: day.date,
        drops: day.type2.drops_details
      });
      setShowDropsModal(true);
    }
  };

  const handleUpdateDistanceSupplement = async (date, routeName, currentValue) => {
    // Create unique key for this route
    const routeKey = `${date}_${routeName || ''}`;
    
    // Toggle editing for this specific route
    if (editingDistanceSupplement[routeKey]) {
      // Save the value
      const inputElement = document.getElementById(`distance-supplement-${routeKey}`);
      const inputValue = inputElement?.value?.trim() || '';
      
      // If empty, use 0
      const newAmount = inputValue === '' ? '0' : inputValue;
      
      // Parse the value - handle negative numbers properly
      const amountValue = parseFloat(newAmount.replace(',', '.'));
      if (isNaN(amountValue)) {
        alert('סכום לא תקין - יש להזין מספר בלבד (אפשר גם מספר שלילי עם מינוס)');
        return;
      }

      setUpdatingDistanceSupplement(true);
      try {
        await api.put(`/salaries/update-distance-supplement/${selectedDriver}/${date}`, {
          amount: amountValue,
          routeName: routeName || null
        });
        
        // Clear editing state
        setEditingDistanceSupplement(prev => ({...prev, [routeKey]: false}));
        
        // Reload the driver report to show updated values
        await loadDriverReport(selectedDriver);
        alert('תוספת מרחק עודכנה בהצלחה');
      } catch (error) {
        alert(error.response?.data?.error || 'שגיאה בעדכון תוספת מרחק');
      } finally {
        setUpdatingDistanceSupplement(false);
      }
    } else {
      // Start editing for this route
      setEditingDistanceSupplement(prev => ({...prev, [routeKey]: true}));
    }
  };

  const handleFinalizeMonth = async () => {
    const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך לנעול את חודש ${monthNames[month - 1]} ${year}?\n\n` +
      `פעולה זו תשמור את המשכורות הנוכחיות עם התמחור הקיים.\n` +
      `שינויים עתידיים בתמחור לא ישפיעו על חודש זה.`
    );

    if (!confirmed) return;

    setFinalizingMonth(true);
    try {
      const response = await api.post(`/salaries/finalize/${month}/${year}`);
      alert(`החודש נעול בהצלחה! ${response.data.finalized_count} נהגים נעולים.`);
      loadSummary(); // Reload to show updated status
    } catch (error) {
      alert(error.response?.data?.error || 'שגיאה בנעילת חודש');
    } finally {
      setFinalizingMonth(false);
    }
  };

  const handleUnfinalizeMonth = async () => {
    const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך לבטל את נעילת חודש ${monthNames[month - 1]} ${year}?\n\n` +
      `פעולה זו תאפשר חישוב מחדש עם תמחור עדכני.`
    );

    if (!confirmed) return;

    setFinalizingMonth(true);
    try {
      const response = await api.post(`/salaries/unfinalize/${month}/${year}`);
      alert(`נעילת החודש בוטלה בהצלחה! ${response.data.unfinalized_count} נהגים שוחררו.`);
      loadSummary(); // Reload to show updated status
    } catch (error) {
      alert(error.response?.data?.error || 'שגיאה בביטול נעילת חודש');
    } finally {
      setFinalizingMonth(false);
    }
  };

  const handleRecalculate = async () => {
    const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
    const confirmed = window.confirm(
      `האם לחשב מחדש את משכורות ${monthNames[month - 1]} ${year}?\n\n` +
      `פעולה זו תחשב מחדש את כל המשכורות עם הנתונים והתמחור העדכניים.`
    );

    if (!confirmed) return;

    setRecalculating(true);
    try {
      const response = await api.post(`/salaries/calculate/${month}/${year}`);
      alert(`חישוב הושלם בהצלחה! ${response.data.driversProcessed} נהגים עובדו.`);
      loadSummary(); // Reload to show updated calculations
    } catch (error) {
      alert(error.response?.data?.error || 'שגיאה בחישוב מחדש');
    } finally {
      setRecalculating(false);
    }
  };

  const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-nexolog-navy">דוחות חודשיים</h1>

        {/* Period Selector */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">חודש</label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal"
              >
                {monthNames.map((name, index) => (
                  <option key={index} value={index + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">שנה</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal"
              />
            </div>
            <button
              onClick={loadSummary}
              disabled={loading}
              className="bg-nexolog-teal hover:bg-nexolog-teal/90 text-white font-medium px-8 py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'טוען...' : 'טען דוח'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500">נהגים</h3>
                <p className="text-3xl font-bold text-nexolog-navy mt-2">{summary.totalDrivers}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500">סה"כ עמלות</h3>
                <p className="text-3xl font-bold text-nexolog-teal mt-2">
                  ₪{summary.totalSalary.toLocaleString('he-IL')}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500">ממוצע עמלה</h3>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  ₪{(summary.totalSalary / summary.totalDrivers).toLocaleString('he-IL', {maximumFractionDigits: 0})}
                </p>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Finalized Status Badge */}
                {isFinalized && (
                  <div className="flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-lg">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">חודש נעול 🔒</span>
                    {finalizedInfo?.drivers?.[0]?.finalized_at && (
                      <span className="text-sm">
                        ({new Date(finalizedInfo.drivers[0].finalized_at).toLocaleDateString('he-IL')})
                      </span>
                    )}
                  </div>
                )}
                
                {!isFinalized && (
                  <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">חודש לא נעול 🔓</span>
                    <span className="text-sm">(שינויי תמחור ישפיעו)</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 mr-auto">
                  <button
                    onClick={handleRecalculate}
                    disabled={recalculating || isFinalized}
                    className="bg-purple-500 hover:bg-purple-600 text-white font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
                    title={isFinalized ? 'יש לבטל נעילה כדי לחשב מחדש' : 'חשב מחדש משכורות'}
                  >
                    {recalculating ? '🔄 מחשב...' : '🔄 חשב מחדש'}
                  </button>
                  
              <button
                onClick={handleExport}
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-lg transition-colors"
              >
                📥 ייצא לאקסל
              </button>
                  
                  {!isFinalized ? (
                    <button
                      onClick={handleFinalizeMonth}
                      disabled={finalizingMonth}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {finalizingMonth ? 'נועל...' : '🔒 נעל חודש'}
                    </button>
                  ) : (
                    <button
                      onClick={handleUnfinalizeMonth}
                      disabled={finalizingMonth}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {finalizingMonth ? 'משחרר...' : '🔓 בטל נעילה'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Drivers Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">נהג</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">ימי עבודה</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">ק"מ</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">דרופים</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">עמלה</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summary.drivers.map((driver) => (
                      <tr 
                        key={driver.driver_id}
                        onClick={() => loadDriverReport(driver.driver_id)}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {driver.driver_name}
                          <span className="ml-2 text-xs text-gray-400">👆 לחץ לפרטים</span>
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{driver.total_days_worked}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{driver.total_km?.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{driver.total_drops}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-nexolog-teal">
                        ₪{parseFloat(driver.calculated_salary).toLocaleString('he-IL')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}

        {/* Driver Detailed Report Modal */}
        {selectedDriver && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-nexolog-navy">
                  {loadingReport ? 'טוען דוח...' : driverReport ? `דוח מפורט - ${driverReport.driver.name}` : 'דוח מפורט'}
                </h2>
                <button
                  onClick={closeDriverReport}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              {loadingReport ? (
                <div className="p-8 text-center text-gray-600">טוען...</div>
              ) : driverReport ? (
                <div className="p-6">
                  {/* Driver Info */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-bold text-lg mb-2">{driverReport.driver.name}</h3>
                    <p className="text-sm text-gray-600">{driverReport.driver.email}</p>
                    <p className="text-sm text-gray-600">{driverReport.driver.phone}</p>
                  </div>

                  {/* Daily Breakdown Table */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-4">פירוט יומי</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">תאריך</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">קווים</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">ק"מ</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">דרופים</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">קבלן משנה</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">נהג מסחרית</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">תוספת מרחק</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סה"כ יום</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {driverReport.daily_breakdown.map((day, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatLocalDate(day.date)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                                {day.route_name ? (
                                  <span title={day.route_name} className="block truncate">
                                    {day.route_name}
                                  </span>
                                ) : day.route_names && day.route_names.length > 0 ? (
                                  <span title={day.route_names.join(', ')} className="block truncate">
                                    {day.route_names.join(', ')}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {day.type1.km.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {day.type2.drops > 0 ? (
                                  <button
                                    onClick={() => handleShowDropsDetails(day)}
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-semibold cursor-pointer"
                                    title="לחץ לפירוט משלוחים"
                                  >
                                    {day.type2.drops} 📦
                                  </button>
                                ) : (
                                  <span className="text-gray-500">{day.type2.drops}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                ₪{day.type1.total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                ₪{day.type2.total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex items-center gap-2">
                                  {editingDistanceSupplement[`${day.date}_${day.route_name || ''}`] ? (
                                    <>
                                      <input
                                        id={`distance-supplement-${day.date}_${day.route_name || ''}`}
                                        type="text"
                                        placeholder={day.distance_supplement?.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                                        className="w-28 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                                        autoFocus
                                        defaultValue={day.distance_supplement !== 0 ? day.distance_supplement : ''}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateDistanceSupplement(day.date, day.route_name, day.distance_supplement || 0)}
                                        disabled={updatingDistanceSupplement}
                                        className="text-green-600 hover:text-green-800 hover:bg-green-100 text-xs bg-green-50 px-2 py-1 rounded transition-colors disabled:opacity-50 cursor-pointer"
                                        title="שמור"
                                      >
                                        ✓
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingDistanceSupplement(prev => ({...prev, [`${day.date}_${day.route_name || ''}`]: false}))}
                                        className="text-red-600 hover:text-red-800 hover:bg-red-100 text-xs bg-red-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                        title="בטל"
                                      >
                                        ✗
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-gray-900">₪{day.distance_supplement?.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</span>
                                      {!isFinalized && (
                                        <button
                                          type="button"
                                          onClick={() => handleUpdateDistanceSupplement(day.date, day.route_name, day.distance_supplement || 0)}
                                          disabled={updatingDistanceSupplement}
                                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 text-xs bg-blue-50 px-2 py-1 rounded transition-colors disabled:opacity-50 cursor-pointer"
                                          title="ערוך תוספת מרחק"
                                        >
                                          ✏️
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-nexolog-teal">
                                ₪{day.total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold">
                          <tr>
                            <td className="px-4 py-3 text-sm">סה"כ</td>
                            <td className="px-4 py-3 text-sm"></td>
                            <td className="px-4 py-3 text-sm">{driverReport.totals.total_km.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="px-4 py-3 text-sm">{driverReport.totals.total_drops.toLocaleString('he-IL')}</td>
                            <td className="px-4 py-3 text-sm">₪{driverReport.totals.type1_total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="px-4 py-3 text-sm">₪{driverReport.totals.type2_total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="px-4 py-3 text-sm">₪{driverReport.totals.total_distance_supplement.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="px-4 py-3 text-sm text-nexolog-teal">
                              ₪{driverReport.totals.grand_total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Totals Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">ימי עבודה</p>
                      <p className="text-2xl font-bold text-blue-600">{driverReport.totals.days}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">סה"כ ק"מ</p>
                      <p className="text-2xl font-bold text-green-600">{driverReport.totals.total_km.toFixed(2)}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">סה"כ דרופים</p>
                      <p className="text-2xl font-bold text-purple-600">{driverReport.totals.total_drops}</p>
                    </div>
                    <div className="bg-nexolog-teal bg-opacity-10 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">סה"כ עמלה</p>
                      <p className="text-2xl font-bold text-nexolog-teal">
                        ₪{driverReport.totals.grand_total.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </p>
                    </div>
                  </div>

                  {/* Download Excel and Send Email Buttons */}
                  <div className="mt-6 flex gap-4">
                    <button
                      onClick={() => {
                        // Download Excel - use token in query param for browser download
                        const token = localStorage.getItem('token');
                        if (!token) {
                          alert('נדרש להתחבר מחדש');
                          window.location.href = '/login';
                          return;
                        }
                        const baseURL = api.defaults.baseURL || '/api';
                        const url = `${baseURL}/salaries/download-excel/${driverReport.driver.id}/${month}/${year}?token=${encodeURIComponent(token)}`;
                        window.location.href = url;
                      }}
                      className="font-medium px-6 py-3 rounded-lg transition-colors bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      📥 הורד Excel
                    </button>
                    <button
                      onClick={async () => {
                        if (!driverReport.driver.email) {
                          alert('לנהג אין כתובת אימייל. אנא עדכן את פרטי הנהג.');
                          return;
                        }
                        if (sendingEmail) {
                          return; // Prevent multiple clicks
                        }
                        setSendingEmail(true);
                        try {
                          const response = await api.post(`/salaries/send-email/${driverReport.driver.id}/${month}/${year}`);
                          alert(response.data?.message || 'אימייל נשלח בהצלחה עם קובץ Excel מצורף');
                        } catch (error) {
                          alert(error.response?.data?.error || error.response?.data?.details || 'שגיאה בשליחת אימייל');
                        } finally {
                          setSendingEmail(false);
                        }
                      }}
                      disabled={!driverReport.driver.email || sendingEmail}
                      className={`font-medium px-6 py-3 rounded-lg transition-colors ${
                        driverReport.driver.email && !sendingEmail
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {sendingEmail ? '⏳ שולח...' : `📧 שלח דוח באימייל ${driverReport.driver.email ? '(עם Excel)' : '(אין אימייל)'}`}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Drops Details Modal */}
        {showDropsModal && selectedDayDrops && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDropsModal(false)}>
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex justify-between items-center mb-4 border-b pb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    פירוט משלוחים - {formatLocalDate(selectedDayDrops.date)}
                  </h3>
                  <button
                    onClick={() => setShowDropsModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                {/* Drops List */}
                <div className="overflow-y-auto max-h-[60vh]">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מזהה משלוח</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">לקוח</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מחיר</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סוג תמחור</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedDayDrops.drops.map((drop, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{drop.delivery_id}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{drop.customer || '(לא צוין)'}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-green-600">₪{drop.price.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              drop.price_type === 'sku_specific' ? 'bg-purple-100 text-purple-800' :
                              drop.price_type === 'client_specific' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {drop.price_type === 'sku_specific' ? 'SKU ייחודי' :
                               drop.price_type === 'client_specific' ? 'לקוח ייחודי' :
                               'בסיס'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-bold">
                      <tr>
                        <td className="px-4 py-3 text-sm" colSpan="2">סה"כ</td>
                        <td className="px-4 py-3 text-sm text-green-600">
                          ₪{selectedDayDrops.drops.reduce((sum, d) => sum + d.price, 0).toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {selectedDayDrops.drops.length.toLocaleString('he-IL')} משלוחים
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Modal Footer */}
                <div className="mt-6 pt-4 border-t flex justify-end">
                  <button
                    onClick={() => setShowDropsModal(false)}
                    className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    סגור
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;

